import React, {useEffect, useState} from 'react';
import {CharacterRosterEntryDTO, GameStateDTO, GameSummaryDTO, PlayerCharacterDTO} from '@hexcrawl/shared-utils';
import {useLobbyStore} from '../../stores/lobbyStore';
import {useAdventureStore} from '../../stores/adventureStore';
import {CharacterCard, CHARACTER_CARD_OUTER_WIDTH_PX} from './CharacterCard';
import {CharacterCreation} from '../character/CharacterCreation';
import {NpcRoster} from './NpcRoster';
import {AdventureSelect} from './AdventureSelect';
import {gameService} from '../../services/gameService';
import {ApiError} from '../../services/api';
import Modal from '../common/Modal';
import {MarketingOptInPrompt} from '../marketing/MarketingOptInPrompt';
import {AccountLinkPrompt} from '../auth/AccountLinkPrompt';
import {useAuthStore} from '../../stores/authStore';

const ROSTER_SLOTS = 3;
const ROSTER_GAP_PX = 20;

/**
 * Width of a full roster row. Anything stacked above the cards is capped to this so it lines up with
 * them rather than stretching to the modal's full width on a wide screen.
 */
const ROSTER_ROW_WIDTH_PX =
  ROSTER_SLOTS * CHARACTER_CARD_OUTER_WIDTH_PX + (ROSTER_SLOTS - 1) * ROSTER_GAP_PX;

interface Props {
  onGameLoaded: (gameState: GameStateDTO) => void;
  onAdventureStarting: (message: string) => void;
  onAdventureError: () => void;
  /** Opens the account panel, for the account-link prompt's primary action. */
  onManageEmail: () => void;
}

export const LobbyScreen: React.FC<Props> = ({onGameLoaded, onAdventureStarting, onAdventureError, onManageEmail}) => {
  const {roster, isLoading, error, fetchRoster} = useLobbyStore();
  const showAccountLinkPrompt = useAuthStore((s) => Boolean(s.user?.showAccountLinkPrompt));
  const [actionError, setActionError] = useState<string | null>(null);

  // null = create new character, PlayerCharacterDTO = resume existing, undefined = closed
  const [creationChar, setCreationChar] = useState<PlayerCharacterDTO | null | undefined>(undefined);
  const [npcRosterChar, setNpcRosterChar] = useState<CharacterRosterEntryDTO | null>(null);
  // Character awaiting adventure selection before their game starts
  const [adventureSelectChar, setAdventureSelectChar] = useState<CharacterRosterEntryDTO | null>(null);
  const isCreationOpen = creationChar !== undefined;

  useEffect(() => {
    fetchRoster();
  }, []);

  const rosterFull = roster.length >= ROSTER_SLOTS;

  // Pad to exactly ROSTER_SLOTS slots
  const slots: (CharacterRosterEntryDTO | null)[] = [
    ...roster,
    ...Array(Math.max(0, ROSTER_SLOTS - roster.length)).fill(null),
  ].slice(0, ROSTER_SLOTS);

  // Begin Adventure opens the adventure picker; the game starts once one is chosen.
  const handleBeginAdventure = (characterId: string) => {
    const char = roster.find(c => c.id === characterId);
    if (!char) return;
    setActionError(null);
    setAdventureSelectChar(char);
    // Pre-fetch adventures filtered by this character's level so they're ready when the modal mounts.
    useAdventureStore.getState().fetchAdventures(char.level);
  };

  const handleAdventureChosen = async (adventureId: string) => {
    const char = adventureSelectChar;
    if (!char) return;
    onAdventureStarting('Starting adventure...');
    setActionError(null);
    try {
      const gameState = await gameService.newGame(char.id, adventureId);
      onGameLoaded(gameState);
      // Do NOT clear loading — downstream flow (map creation / MapScene) owns it from here.
    } catch (e) {
      onAdventureError();
      const code = e instanceof ApiError ? e.serverCode : undefined;
      if (code === 'CHARACTER_IN_ACTIVE_GAME') {
        setActionError('This character is already on an adventure. Continue their current game or wait for it to end.');
      } else {
        setActionError(e instanceof Error ? e.message : 'Failed to start adventure');
      }
    }
  };

  const handleContinueAdventure = async (game: GameSummaryDTO) => {
    onAdventureStarting('Loading adventure...');
    setActionError(null);
    try {
      const gameState = await gameService.getState(game.id);
      onGameLoaded(gameState);
      // Do NOT clear loading — MapScene owns it from here.
    } catch (e: any) {
      onAdventureError();
      setActionError(e?.message || 'Failed to load adventure');
    }
  };

  const handleCreateCharacter = () => {
    if (rosterFull) return;
    setCreationChar(null);
  };

  const handleCreationComplete = () => {
    setCreationChar(undefined);
    fetchRoster();
  };

  const busy = isLoading;

  if (isCreationOpen) {
    return (
      <CharacterCreation
        initialCharacter={creationChar ?? undefined}
        onComplete={handleCreationComplete}
      />
    );
  }

  if (npcRosterChar) {
    return (
      <NpcRoster
        playerCharacterId={npcRosterChar.id}
        playerCharacterName={npcRosterChar.name}
        onClose={() => setNpcRosterChar(null)}
      />
    );
  }

  if (adventureSelectChar) {
    return (
      <AdventureSelect
        characterName={adventureSelectChar.name}
        onConfirm={handleAdventureChosen}
        onCancel={() => setAdventureSelectChar(null)}
      />
    );
  }

  // Empty state — no characters yet
  if (!isLoading && roster.length === 0) {
    return (
      <Modal isOpen={true} onClose={undefined}>
        <div style={{width: 360, fontFamily: 'monospace', textAlign: 'center', padding: '8px 0'}}>
          <div style={{fontSize: 18, fontWeight: 'bold', color: '#e8e8ff', marginBottom: 8}}>
            Choose Your Adventurer
          </div>
          <div style={{fontSize: 36, margin: '24px 0 12px', opacity: 0.25}}>⚔️</div>
          <div style={{color: '#888', fontSize: 13, marginBottom: 8}}>
            You have no characters yet.
          </div>
          <div style={{color: '#666', fontSize: 12, marginBottom: 28}}>
            Create a character to begin your first adventure.
          </div>
          <button
            onClick={handleCreateCharacter}
            disabled={busy}
            style={{
              background: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              padding: '10px 24px',
              fontFamily: 'monospace',
              fontSize: 14,
              fontWeight: 'bold',
              cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            + Create Your First Character
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={true} onClose={undefined}>
      <div style={{
          maxWidth: 'calc(100vw - 48px)',
          maxHeight: '75vh',
          overflowY: 'auto',
          fontFamily: 'monospace',
          boxSizing: 'border-box',
        }}>
        <div style={{fontSize: 18, fontWeight: 'bold', color: '#e8e8ff', marginBottom: 20, textAlign: 'center'}}>
          Choose Your Adventurer
        </div>

        {/*
          * Roster branch only — never over character creation, the NPC roster, or adventure select.
          *
          * One banner at a time, and the account-link ask wins. Through Phase 2 the two cannot both
          * qualify (one needs an address on file, the other needs none), but Phase 3 drops that
          * requirement, and two stacked asks read as spam — so the choice is made here rather than
          * left to coincidence.
          */}
        <div style={{maxWidth: ROSTER_ROW_WIDTH_PX, margin: '0 auto'}}>
          {showAccountLinkPrompt
            ? <AccountLinkPrompt onManageEmail={onManageEmail}/>
            : <MarketingOptInPrompt/>}
        </div>

        {isLoading && (
          <div style={{color: '#888', fontSize: 14, textAlign: 'center', padding: '24px 0'}}>
            Loading Roster…
          </div>
        )}

        {(error || actionError) && (
          <div style={{color: '#f88', fontSize: 13, marginBottom: 16, textAlign: 'center'}}>
            {error || actionError}
            {error && (
              <button
                onClick={() => { fetchRoster(); }}
                style={{marginLeft: 12, background: 'none', border: '1px solid #f88', color: '#f88', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 12}}
              >
                Retry
              </button>
            )}
            {actionError && (
              <button
                onClick={() => setActionError(null)}
                style={{marginLeft: 12, background: 'none', border: '1px solid #f88', color: '#f88', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 12}}
              >
                Dismiss
              </button>
            )}
          </div>
        )}

        {!isLoading && (
          <div style={{display: 'flex', flexWrap: 'wrap', gap: ROSTER_GAP_PX, justifyContent: 'center'}}>
            {slots.map((char, idx) => (
              <CharacterCard
                key={char?.id ?? `empty-${idx}`}
                character={char}
                onCreateCharacter={handleCreateCharacter}
                onResumeCreation={(c) => setCreationChar(c)}
                onBeginAdventure={handleBeginAdventure}
                onContinueAdventure={handleContinueAdventure}
                onViewNpcs={(c) => setNpcRosterChar(c)}
                disabled={busy}
              />
            ))}
          </div>
        )}

        {rosterFull && !isLoading && (
          <div style={{marginTop: 14, textAlign: 'center', fontSize: 11, color: '#555'}}>
            Roster full ({ROSTER_SLOTS}/{ROSTER_SLOTS}) — retire or complete an adventure to free a slot.
          </div>
        )}

      </div>
    </Modal>
  );
};
